/**
 * Formats a timestamp string as a relative "time ago" phrase, e.g. "3 minutes
 * ago" or "just now". Deterministic (no locale / timezone dependency).
 *
 * Coarsens past a week into weeks, months, and years, and only ever looks
 * backwards. `formatElapsed` is the sibling that stops at days and also runs
 * forwards, for an operator surface comparing rows against each other.
 *
 * @example formatTimeAgo('2024-03-15T10:00:00') // "4 hours ago" at 14:00 same day
 * @example formatTimeAgo('2024-03-13T10:00:00') // "2 days ago" on 2024-03-15
 */
export default function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  } else if (diffMonths < 1) {
    return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
  } else if (diffMonths < 12) {
    return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
  } else {
    return `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
  }
}
